import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Plus, 
  Pause, 
  Play, 
  Trash2, 
  Clock,
  Calendar,
  Terminal,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Pencil,
  Save
} from 'lucide-react';

// Mock data for scheduled jobs
const INITIAL_JOBS = [
  {
    id: '1',
    name: 'Analytics Calculation Cron',
    purpose: 'This API calculates and stores the following daily counts for the current day in the database:\n• Total inbound calls\n• Total outbound calls\n• Total SMS\n• Total web agent interactions\n\nThe calculated result must be saved into the database for reporting and analytics purposes.',
    scheduleType: 'daily',
    scheduleTime: '23:45',
    scheduleDays: [],
    apiEndpoint: "curl --location 'https://autoshopllm.ncrts.pro/v1/analytics/calculate-daily' \\\n--header 'Content-Type: application/json'",
    status: 'active',
    lastRun: '2025-01-10T23:45:00',
    lastRunStatus: 'success', // 'success' | 'error'
    nextRun: '2025-01-11T23:45:00',
  },
  {
    id: '2',
    name: 'Database Backup',
    purpose: 'Creates a full backup of the MongoDB database and uploads it to cloud storage for disaster recovery purposes.',
    scheduleType: 'daily',
    scheduleTime: '02:00',
    scheduleDays: [],
    apiEndpoint: "curl --location 'https://autoshopllm.ncrts.pro/v1/system/backup' \\\n--header 'Content-Type: application/json'",
    status: 'active',
    lastRun: '2025-01-11T02:00:00',
    lastRunStatus: 'success',
    nextRun: '2025-01-12T02:00:00',
  },
  {
    id: '3',
    name: 'Weekly Report Generation',
    purpose: 'Generates weekly performance reports for all shops and sends them via email to shop admins.',
    scheduleType: 'weekly',
    scheduleTime: '09:00',
    scheduleDays: ['monday'],
    apiEndpoint: "curl --location 'https://autoshopllm.ncrts.pro/v1/reports/weekly' \\\n--header 'Content-Type: application/json'",
    status: 'paused',
    lastRun: '2025-01-06T09:00:00',
    lastRunStatus: 'error',
    nextRun: null,
  },
];

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

// Generate time options for the time picker (every 15 minutes)
const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    const hour = h.toString().padStart(2, '0');
    const minute = m.toString().padStart(2, '0');
    const value = `${hour}:${minute}`;
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h % 12 || 12;
    const label = `${displayHour}:${minute.padStart(2, '0')} ${period}`;
    TIME_OPTIONS.push({ value, label });
  }
}

const ScheduledJobs = () => {
  const [jobs, setJobs] = useState(INITIAL_JOBS);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  
  // Confirmation dialog states
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'pause' | 'activate' | 'delete', job: Job }
  
  // New job form state
  const [newJob, setNewJob] = useState({
    name: '',
    purpose: '',
    scheduleType: 'daily',
    scheduleTime: '00:00',
    scheduleDays: [],
    apiEndpoint: '',
  });
  
  // Edit job form state
  const [editJob, setEditJob] = useState(null);
  
  const [formErrors, setFormErrors] = useState({});

  // Validate form
  const validateForm = (jobData) => {
    const errors = {};
    if (!jobData.name.trim()) errors.name = 'Job name is required';
    if (!jobData.purpose.trim()) errors.purpose = 'Purpose is required';
    if (!jobData.scheduleTime) errors.scheduleTime = 'Schedule time is required';
    if (jobData.scheduleType === 'weekly' && jobData.scheduleDays.length === 0) {
      errors.scheduleDays = 'Select at least one day';
    }
    if (!jobData.apiEndpoint.trim()) errors.apiEndpoint = 'API/Curl command is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Add new job
  const handleAddJob = () => {
    if (!validateForm(newJob)) return;
    
    const job = {
      id: Date.now().toString(),
      ...newJob,
      status: 'active',
      lastRun: null,
      lastRunStatus: null,
      nextRun: calculateNextRun(newJob.scheduleType, newJob.scheduleTime, newJob.scheduleDays),
    };
    
    setJobs([...jobs, job]);
    setShowAddDialog(false);
    resetForm();
  };

  // Save edited job
  const handleSaveEdit = () => {
    if (!validateForm(editJob)) return;
    
    setJobs(jobs.map(j => j.id === editJob.id ? {
      ...editJob,
      nextRun: editJob.status === 'active' 
        ? calculateNextRun(editJob.scheduleType, editJob.scheduleTime, editJob.scheduleDays)
        : null
    } : j));
    
    setShowEditDialog(false);
    setEditJob(null);
    setFormErrors({});
  };

  // Calculate next run time (simplified)
  const calculateNextRun = (type, time, days) => {
    const now = new Date();
    const [hours, minutes] = time.split(':').map(Number);
    const next = new Date(now);
    next.setHours(hours, minutes, 0, 0);
    
    if (next <= now) {
      next.setDate(next.getDate() + 1);
    }
    
    return next.toISOString();
  };

  // Reset form
  const resetForm = () => {
    setNewJob({
      name: '',
      purpose: '',
      scheduleType: 'daily',
      scheduleTime: '00:00',
      scheduleDays: [],
      apiEndpoint: '',
    });
    setFormErrors({});
  };

  // Handle pause/activate job
  const handleToggleStatus = (job) => {
    const action = job.status === 'active' ? 'pause' : 'activate';
    setConfirmAction({ type: action, job });
  };

  // Handle delete job
  const handleDeleteJob = (job) => {
    setConfirmAction({ type: 'delete', job });
    setShowViewDialog(false);
  };

  // Handle edit job
  const handleEditJob = (job) => {
    setEditJob({ ...job });
    setShowViewDialog(false);
    setShowEditDialog(true);
  };

  // Confirm action
  const executeConfirmedAction = () => {
    if (!confirmAction) return;
    
    const { type, job } = confirmAction;
    
    if (type === 'delete') {
      setJobs(jobs.filter(j => j.id !== job.id));
    } else if (type === 'pause') {
      setJobs(jobs.map(j => j.id === job.id ? { ...j, status: 'paused', nextRun: null } : j));
    } else if (type === 'activate') {
      setJobs(jobs.map(j => j.id === job.id ? { 
        ...j, 
        status: 'active', 
        nextRun: calculateNextRun(j.scheduleType, j.scheduleTime, j.scheduleDays) 
      } : j));
    }
    
    setConfirmAction(null);
  };

  // View job details
  const handleViewJob = (job) => {
    setSelectedJob(job);
    setShowViewDialog(true);
  };

  // Format schedule display
  const formatSchedule = (job) => {
    if (job.scheduleType === 'daily') {
      return `Every day at ${formatTime(job.scheduleTime)}`;
    } else if (job.scheduleType === 'weekly') {
      const days = job.scheduleDays.map(d => DAYS_OF_WEEK.find(day => day.value === d)?.label).join(', ');
      return `Weekly on ${days} at ${formatTime(job.scheduleTime)}`;
    } else if (job.scheduleType === 'multiple') {
      return `Multiple times daily at ${formatTime(job.scheduleTime)}`;
    }
    return 'Unknown schedule';
  };

  // Format time to 12-hour format
  const formatTime = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Toggle day selection for new job
  const toggleDay = (day, isEdit = false) => {
    if (isEdit) {
      setEditJob(prev => ({
        ...prev,
        scheduleDays: prev.scheduleDays.includes(day)
          ? prev.scheduleDays.filter(d => d !== day)
          : [...prev.scheduleDays, day]
      }));
    } else {
      setNewJob(prev => ({
        ...prev,
        scheduleDays: prev.scheduleDays.includes(day)
          ? prev.scheduleDays.filter(d => d !== day)
          : [...prev.scheduleDays, day]
      }));
    }
  };

  // Time Picker Component
  const TimePicker = ({ value, onChange, error }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] ${error ? 'border-[#EF4444]' : ''}`}>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#64748B]" />
          <SelectValue placeholder="Select time" />
        </div>
      </SelectTrigger>
      <SelectContent className="bg-[#1E293B] border-[#334155] max-h-[300px]">
        {TIME_OPTIONS.map(option => (
          <SelectItem 
            key={option.value} 
            value={option.value}
            className="text-[#E2E8F0] focus:bg-[#334155] focus:text-[#E2E8F0]"
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  // Job Form Component (used for both Add and Edit)
  const JobForm = ({ jobData, setJobData, isEdit = false }) => (
    <div className="space-y-5 py-4">
      {/* Job Name */}
      <div className="space-y-2">
        <Label className="text-[#E2E8F0]">Job Name *</Label>
        <Input
          value={jobData.name}
          onChange={(e) => setJobData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="e.g., Analytics Calculation Cron"
          className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] ${formErrors.name ? 'border-[#EF4444]' : ''}`}
        />
        {formErrors.name && <p className="text-[#EF4444] text-xs">{formErrors.name}</p>}
      </div>

      {/* Purpose */}
      <div className="space-y-2">
        <Label className="text-[#E2E8F0]">Purpose *</Label>
        <Textarea
          value={jobData.purpose}
          onChange={(e) => setJobData(prev => ({ ...prev, purpose: e.target.value }))}
          placeholder="Describe what this job does..."
          rows={4}
          className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] resize-none ${formErrors.purpose ? 'border-[#EF4444]' : ''}`}
        />
        {formErrors.purpose && <p className="text-[#EF4444] text-xs">{formErrors.purpose}</p>}
      </div>

      {/* Schedule Type */}
      <div className="space-y-2">
        <Label className="text-[#E2E8F0]">Schedule Type *</Label>
        <Select 
          value={jobData.scheduleType} 
          onValueChange={(value) => setJobData(prev => ({ ...prev, scheduleType: value, scheduleDays: [] }))}
        >
          <SelectTrigger className="bg-[#0F172A] border-[#334155] text-[#E2E8F0]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1E293B] border-[#334155]">
            <SelectItem value="daily" className="text-[#E2E8F0] focus:bg-[#334155] focus:text-[#E2E8F0]">
              Every Day
            </SelectItem>
            <SelectItem value="weekly" className="text-[#E2E8F0] focus:bg-[#334155] focus:text-[#E2E8F0]">
              Weekly (specific days)
            </SelectItem>
            <SelectItem value="multiple" className="text-[#E2E8F0] focus:bg-[#334155] focus:text-[#E2E8F0]">
              Multiple Times Daily
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Weekly Days Selection */}
      {jobData.scheduleType === 'weekly' && (
        <div className="space-y-2">
          <Label className="text-[#E2E8F0]">Select Days *</Label>
          <div className="flex flex-wrap gap-2">
            {DAYS_OF_WEEK.map(day => (
              <Button
                key={day.value}
                type="button"
                size="sm"
                variant={jobData.scheduleDays.includes(day.value) ? 'default' : 'outline'}
                onClick={() => toggleDay(day.value, isEdit)}
                className={jobData.scheduleDays.includes(day.value) 
                  ? 'bg-[#2563EB] text-white' 
                  : 'border-[#334155] text-[#94A3B8] hover:bg-[#334155]'}
              >
                {day.label}
              </Button>
            ))}
          </div>
          {formErrors.scheduleDays && <p className="text-[#EF4444] text-xs">{formErrors.scheduleDays}</p>}
        </div>
      )}

      {/* Schedule Time */}
      <div className="space-y-2">
        <Label className="text-[#E2E8F0]">Time *</Label>
        <TimePicker 
          value={jobData.scheduleTime} 
          onChange={(value) => setJobData(prev => ({ ...prev, scheduleTime: value }))}
          error={formErrors.scheduleTime}
        />
        {formErrors.scheduleTime && <p className="text-[#EF4444] text-xs">{formErrors.scheduleTime}</p>}
      </div>

      {/* API Endpoint / Curl */}
      <div className="space-y-2">
        <Label className="text-[#E2E8F0]">API / Curl Command *</Label>
        <Textarea
          value={jobData.apiEndpoint}
          onChange={(e) => setJobData(prev => ({ ...prev, apiEndpoint: e.target.value }))}
          placeholder="curl --location 'https://api.example.com/endpoint' --header 'Content-Type: application/json'"
          rows={5}
          className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] font-mono text-sm resize-none ${formErrors.apiEndpoint ? 'border-[#EF4444]' : ''}`}
        />
        {formErrors.apiEndpoint && <p className="text-[#EF4444] text-xs">{formErrors.apiEndpoint}</p>}
      </div>
    </div>
  );

  return (
    <div className="p-6 md:p-8" data-testid="scheduled-jobs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#E2E8F0] tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Scheduled Jobs
          </h1>
          <p className="text-[#94A3B8]">
            Manage and monitor cron jobs for the application
          </p>
        </div>
        <Button
          onClick={() => setShowAddDialog(true)}
          className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
          data-testid="add-job-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Job
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#2563EB]/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-[#60A5FA]" />
            </div>
            <div>
              <p className="text-[#94A3B8] text-sm">Total Jobs</p>
              <p className="text-2xl font-bold text-[#E2E8F0]">{jobs.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#22C55E]/20 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-[#4ADE80]" />
            </div>
            <div>
              <p className="text-[#94A3B8] text-sm">Active</p>
              <p className="text-2xl font-bold text-[#E2E8F0]">{jobs.filter(j => j.status === 'active').length}</p>
            </div>
          </div>
        </div>
        <div className="bg-[#1E293B] border border-[#334155] rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
              <Pause className="w-5 h-5 text-[#FBBF24]" />
            </div>
            <div>
              <p className="text-[#94A3B8] text-sm">Paused</p>
              <p className="text-2xl font-bold text-[#E2E8F0]">{jobs.filter(j => j.status === 'paused').length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Jobs Table */}
      <div className="bg-[#1E293B] border border-[#334155] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-[#334155] hover:bg-transparent">
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider w-[300px] min-w-[300px]">Job Name</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">Schedule</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">Last Run</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider">Next Run</TableHead>
                <TableHead className="text-[#94A3B8] font-semibold text-xs uppercase tracking-wider text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-[#64748B]">
                    No scheduled jobs found. Click "Add New Job" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                jobs.map((job) => (
                  <TableRow 
                    key={job.id} 
                    className="border-b border-[#334155]/50 hover:bg-[#334155]/20 cursor-pointer"
                    onClick={() => handleViewJob(job)}
                    data-testid={`job-row-${job.id}`}
                  >
                    <TableCell className="font-medium text-[#E2E8F0] w-[300px] min-w-[300px]">
                      <div className="flex items-center gap-2">
                        <Terminal className="w-4 h-4 text-[#64748B] flex-shrink-0" />
                        <span className="truncate">{job.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-[#94A3B8]">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#64748B]" />
                        {formatSchedule(job)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={`border ${job.status === 'active' 
                          ? 'bg-[#22C55E]/20 text-[#4ADE80] border-[#22C55E]/30' 
                          : 'bg-[#F59E0B]/20 text-[#FBBF24] border-[#F59E0B]/30'}`}
                      >
                        {job.status === 'active' ? 'Active' : 'Paused'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-[#94A3B8] text-sm">
                      <div className="flex items-center gap-2">
                        {job.lastRun && job.lastRunStatus && (
                          <div 
                            className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                              job.lastRunStatus === 'success' 
                                ? 'bg-[#22C55E]' 
                                : 'bg-[#F59E0B]'
                            }`}
                            title={job.lastRunStatus === 'success' ? 'Completed successfully' : 'Completed with errors'}
                          />
                        )}
                        {formatDate(job.lastRun)}
                      </div>
                    </TableCell>
                    <TableCell className="text-[#94A3B8] text-sm">
                      {job.status === 'active' ? formatDate(job.nextRun) : '-'}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleStatus(job)}
                          className={`h-8 px-3 ${job.status === 'active' 
                            ? 'border-[#F59E0B]/30 text-[#FBBF24] hover:bg-[#F59E0B]/20' 
                            : 'border-[#22C55E]/30 text-[#4ADE80] hover:bg-[#22C55E]/20'}`}
                          data-testid={`toggle-job-${job.id}`}
                        >
                          {job.status === 'active' ? (
                            <>
                              <Pause className="w-3.5 h-3.5 mr-1" />
                              Pause
                            </>
                          ) : (
                            <>
                              <Play className="w-3.5 h-3.5 mr-1" />
                              Activate
                            </>
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Note about mock data */}
      <div className="mt-6 p-4 bg-[#334155]/30 border border-[#334155] rounded-lg">
        <p className="text-[#94A3B8] text-sm">
          <span className="text-[#F59E0B] font-medium">Note:</span> This page is using mock data for UI demonstration. Backend cron job management API integration pending.
        </p>
      </div>

      {/* Add Job Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="bg-[#1E293B] border-[#334155] text-[#E2E8F0] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#E2E8F0] text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Add New Scheduled Job
            </DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              Configure a new cron job to run at scheduled intervals
            </DialogDescription>
          </DialogHeader>
          
          <JobForm jobData={newJob} setJobData={setNewJob} isEdit={false} />

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowAddDialog(false); resetForm(); }}
              className="border-[#334155] text-[#94A3B8] hover:bg-[#334155]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddJob}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Job Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => { setShowEditDialog(open); if (!open) { setEditJob(null); setFormErrors({}); } }}>
        <DialogContent className="bg-[#1E293B] border-[#334155] text-[#E2E8F0] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#E2E8F0] text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Edit Scheduled Job
            </DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              Modify the job configuration
            </DialogDescription>
          </DialogHeader>
          
          {editJob && <JobForm jobData={editJob} setJobData={setEditJob} isEdit={true} />}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowEditDialog(false); setEditJob(null); setFormErrors({}); }}
              className="border-[#334155] text-[#94A3B8] hover:bg-[#334155]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              className="bg-[#22C55E] hover:bg-[#16A34A] text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Job Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="bg-[#1E293B] border-[#334155] text-[#E2E8F0] max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedJob && (
            <>
              <DialogHeader className="relative">
                {/* Delete button at top right */}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDeleteJob(selectedJob)}
                  className="absolute right-0 top-0 h-8 w-8 p-0 text-[#F87171] hover:bg-[#EF4444]/20 hover:text-[#F87171]"
                  data-testid="delete-job-from-view"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                
                <div className="flex items-center gap-3 pr-10">
                  <div className="w-10 h-10 rounded-lg bg-[#2563EB]/20 flex items-center justify-center">
                    <Terminal className="w-5 h-5 text-[#60A5FA]" />
                  </div>
                  <div>
                    <DialogTitle className="text-[#E2E8F0] text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {selectedJob.name}
                    </DialogTitle>
                    <Badge 
                      variant="outline" 
                      className={`mt-1 border ${selectedJob.status === 'active' 
                        ? 'bg-[#22C55E]/20 text-[#4ADE80] border-[#22C55E]/30' 
                        : 'bg-[#F59E0B]/20 text-[#FBBF24] border-[#F59E0B]/30'}`}
                    >
                      {selectedJob.status === 'active' ? 'Active' : 'Paused'}
                    </Badge>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Purpose Section */}
                <div>
                  <h4 className="text-[#94A3B8] text-sm font-medium mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Purpose
                  </h4>
                  <div className="bg-[#0F172A] border border-[#334155] rounded-lg p-4">
                    <p className="text-[#E2E8F0] text-sm whitespace-pre-line">{selectedJob.purpose}</p>
                  </div>
                </div>

                {/* Schedule Section */}
                <div>
                  <h4 className="text-[#94A3B8] text-sm font-medium mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Schedule
                  </h4>
                  <div className="bg-[#0F172A] border border-[#334155] rounded-lg p-4">
                    <p className="text-[#E2E8F0]">{formatSchedule(selectedJob)}</p>
                    <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-[#64748B]">Last Run</p>
                        <div className="flex items-center gap-2 mt-1">
                          {selectedJob.lastRun && selectedJob.lastRunStatus && (
                            <div 
                              className={`w-2.5 h-2.5 rounded-full ${
                                selectedJob.lastRunStatus === 'success' 
                                  ? 'bg-[#22C55E]' 
                                  : 'bg-[#F59E0B]'
                              }`}
                            />
                          )}
                          <p className="text-[#94A3B8]">{formatDate(selectedJob.lastRun)}</p>
                        </div>
                        {selectedJob.lastRunStatus && (
                          <p className={`text-xs mt-1 ${selectedJob.lastRunStatus === 'success' ? 'text-[#4ADE80]' : 'text-[#FBBF24]'}`}>
                            {selectedJob.lastRunStatus === 'success' ? 'Completed successfully' : 'Completed with errors'}
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-[#64748B]">Next Run</p>
                        <p className="text-[#94A3B8] mt-1">{selectedJob.status === 'active' ? formatDate(selectedJob.nextRun) : '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* API Section */}
                <div>
                  <h4 className="text-[#94A3B8] text-sm font-medium mb-2 flex items-center gap-2">
                    <Terminal className="w-4 h-4" />
                    API / Curl Command
                  </h4>
                  <div className="bg-[#0F172A] border border-[#334155] rounded-lg p-4">
                    <pre className="text-[#60A5FA] text-sm font-mono whitespace-pre-wrap break-all">{selectedJob.apiEndpoint}</pre>
                  </div>
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowViewDialog(false)}
                  className="border-[#334155] text-[#94A3B8] hover:bg-[#334155]"
                >
                  Close
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleEditJob(selectedJob)}
                  className="border-[#2563EB]/30 text-[#60A5FA] hover:bg-[#2563EB]/20"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit Job
                </Button>
                <Button
                  onClick={() => { setShowViewDialog(false); handleToggleStatus(selectedJob); }}
                  className={selectedJob.status === 'active' 
                    ? 'bg-[#F59E0B] hover:bg-[#D97706] text-white'
                    : 'bg-[#22C55E] hover:bg-[#16A34A] text-white'}
                >
                  {selectedJob.status === 'active' ? (
                    <>
                      <Pause className="w-4 h-4 mr-2" />
                      Pause Job
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Activate Job
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent className="bg-[#1E293B] border-[#334155]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#E2E8F0] flex items-center gap-2">
              {confirmAction?.type === 'delete' ? (
                <XCircle className="w-5 h-5 text-[#EF4444]" />
              ) : confirmAction?.type === 'pause' ? (
                <Pause className="w-5 h-5 text-[#F59E0B]" />
              ) : (
                <Play className="w-5 h-5 text-[#22C55E]" />
              )}
              Confirm {confirmAction?.type === 'delete' ? 'Delete' : confirmAction?.type === 'pause' ? 'Pause' : 'Activate'} Job
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#94A3B8]">
              {confirmAction?.type === 'delete' ? (
                <>
                  Are you sure you want to delete the job <span className="text-[#E2E8F0] font-medium">"{confirmAction?.job?.name}"</span>? This action cannot be undone.
                </>
              ) : confirmAction?.type === 'pause' ? (
                <>
                  Are you sure you want to pause the job <span className="text-[#E2E8F0] font-medium">"{confirmAction?.job?.name}"</span>? The job will stop running until reactivated.
                </>
              ) : (
                <>
                  Are you sure you want to activate the job <span className="text-[#E2E8F0] font-medium">"{confirmAction?.job?.name}"</span>? The job will start running according to its schedule.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#334155] text-[#94A3B8] hover:bg-[#334155] hover:text-[#E2E8F0]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={executeConfirmedAction}
              className={
                confirmAction?.type === 'delete' 
                  ? 'bg-[#EF4444] hover:bg-[#DC2626] text-white'
                  : confirmAction?.type === 'pause'
                  ? 'bg-[#F59E0B] hover:bg-[#D97706] text-white'
                  : 'bg-[#22C55E] hover:bg-[#16A34A] text-white'
              }
            >
              {confirmAction?.type === 'delete' ? 'Delete' : confirmAction?.type === 'pause' ? 'Pause' : 'Activate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ScheduledJobs;
